/**
 * Centralized, HIPAA-aware logger for the frontend.
 *
 * Goals:
 *  - Never emit PHI to the browser console in production builds.
 *  - Keep rich logging in development for debugging.
 *
 * Behavior:
 *  - debug / info / log : developer-only. Completely no-op in production.
 *  - warn / error       : always emitted, but in production their arguments
 *                         are sanitized so raw objects/arrays (which routinely
 *                         carry patient data: names, MRNs, DOB, insurance,
 *                         results) are never serialized to the console.
 *
 * Usage:
 *   import logger from '../utils/logger';
 *   logger.debug('fetched orders', data);   // dev only
 *   logger.error('Error fetching orders', error);
 */

const isProd = process.env.NODE_ENV === 'production';

/**
 * Reduce a single argument to something safe to print in production.
 * Primitives are preserved (they're explicit and developer-authored).
 * Errors keep their name + message (useful, low PHI risk).
 * Anything structured is redacted, because it may contain PHI.
 */
const sanitizeArg = (arg) => {
  if (arg === null || arg === undefined) return arg;

  const type = typeof arg;
  if (type === 'string' || type === 'number' || type === 'boolean') {
    return arg;
  }

  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}`;
  }

  return Array.isArray(arg) ? '[redacted array]' : '[redacted object]';
};

const sanitize = (args) => (isProd ? args.map(sanitizeArg) : args);

const noop = () => {};

const logger = {
  // Developer-only levels — silent in production.
  debug: isProd ? noop : (...args) => console.debug(...args),
  info: isProd ? noop : (...args) => console.info(...args),
  log: isProd ? noop : (...args) => console.log(...args),

  // Always emitted, sanitized in production.
  warn: (...args) => console.warn(...sanitize(args)),
  error: (...args) => console.error(...sanitize(args)),
};

export default logger;
